//Library imports
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { useAsync } from 'react-async';
import moment from 'moment';

//Services
import { getDisciplineGroupTherapistsByDisciplineIds } from '../../api/services/therapistRolesService';
import { transferCaseload, getCaseloadsFromArray } from '../../api/services/caseloadService';

//Models and data types
import { selectOption } from '../../types/selectOption';
import Caseload from '../../api/models/caseload';
import CaseloadTransferRequest from '../../api/requests/caseloadTransferRequest';
import { getTherapistByUserId } from '../../api/services/therapistService';
//Components
import Layout from '../../components/layout';
import SubmitButton from '../../components/submitButton';
import DataTable from '../../components/dataTable';
import DatePicker from 'react-datepicker';
import { ResponseStatus } from '../../components/notifications';
import { useHistory, useLocation } from 'react-router-dom';
import { Notification } from '../../components/notifications/index';
import SelectMulti from 'react-select';
import Therapists from '../admin/therapists';



//Get our view state
export type ViewState = {
    message?: string;
    ids: number[];
    disciplineId: number;
    disciplineGroupId: number;
};


/**
     * Methods to fetch search criteria with which to populate
     */
/*const fetchTherapists = async () => {
    return await getTherapists();
};*/

/**
 * Primary Component
 */
const CaseloadsTransferStep2 = () => {
    //Process our DataTable columns and data
    const columns = React.useMemo(
        () => [
            {
                Header: ' ',
                accessor: (s: any) => {
                    return (
                        <input
                            id={`caseload_${s.caseloadId}`}
                            className="datatable-checkbox"
                            type="checkbox"
                            value={s.caseloadId}
                            onClick={(e: any) => {
                                setSelectedCaseloads((prevSelectedCaseloads) => {
                                    let newCurrent = [...prevSelectedCaseloads];
                                    if (e.target.checked) {
                                        newCurrent.push(s.caseloadId);
                                    } else {
                                        newCurrent = newCurrent.filter((c) => c !== s.caseloadId);
                                    }

                                    // Update canTransfer after selecting/deselecting
                                    setCanTransfer(newCurrent.length > 0);
                                    return newCurrent; // return the updated selected caseloads
                                });
                                e.stopPropagation();
                            }}
                        />

                    )
                },
                width: 30,
                disableSortBy: true
            },
            {
                Header: 'Student',
                accessor: (s: any) => `${s.studentLastName}, ${s.studentFirstName}`,
            },
            {
                Header: 'Discipline',
                accessor: (s: any) => s.disciplineName
            },
            {
                Header: 'District',
                accessor: (s: any) => s.districtName,
                width: 250,
            },
            {
                Header: 'School',
                accessor: (s: any) => s.schoolName
            },
            {
                Header: 'Status',
                accessor: (s: any) => s.studentStatus
            },
            {
                Header: 'Grade',
                accessor: (s: any) => s.gradeName,
            },
            {
                Header: 'IEP',
                accessor: (s: any) => s.iepStartDate === null || s.iepEndDate === null ? '' : `${moment(s.iepStartDate).format('l')} - ${moment(s.iepEndDate).format('l')}`,
            },
            {
                Header: 'Therapist Type',
                accessor: (s: any) => s.therapyType,
            },
            {
                Header: 'Primary Therapist',
                accessor: (s: any) => s.therapistName === undefined ? '' : `${s.therapistName}`,
            },
            {
                accessor: 'studentId',
                className: 'hidden',
            },
            {
                accessor: 'caseloadId',
                className: 'hidden',
            },
        ],
        //Disabling next line for eslint only because we need an empty dependency array so the effec tis only called once, on load
        // eslint-disable-next-line
        []
    );

    //App hooks
    const { state } = useLocation<ViewState>();
    const history = useHistory();

    //Select element options
    const [therapistsOptions, setTherapistsOptions] = useState<selectOption[]>([{ value: -1, label: '-' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([{ value: -1, label: '-' }]);
    const [defaultDisciplineOption, setDefaultDisciplineOption] = useState<selectOption>();
    //Data hooks
    const [therapist, setTherapist] = useState<number>(-1);
    const prevTherapistRef = useRef<number | null>(null);
    const [therapistName, setTherapistName] = useState<string>('');
    const [districtName, setDistrictName] = useState<string>('');
    const [schoolName, setSchoolName] = useState<string>('');
    const [disciplineName, setDisciplineName] = useState<string>('');
    const [caseloads, setCaseloads] = useState<Caseload[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [caseloadIds, setCaseloadIds] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [removeDate, setRemoveDate] = useState<Date | null>();
    const [removeOriginal, setRemoveOriginal] = useState<boolean>(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedCaseloads, setSelectedCaseloads] = useState<number[]>([]);
    const [canTransfer, setCanTransfer] = useState<boolean>(false);
    const [isSelect, setIsSelect] = useState<boolean>(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [disciplineId, setDisciplineId] = useState<number>(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [fetchedTherapistsOptions, setFetchedTherapistsOptions] = useState<selectOption[]>([]);
    const [disciplineIds, setDisciplineIds] = useState<number[]>([]);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const todaysDate = new Date();
    const endOfMonth = new Date(todaysDate.getFullYear(), todaysDate.getMonth() + 1, 0);




    /**
     * This effect runs on page load (though technically any time state.ids is updated)
     * We grab the caseload Ids from the app state (previous page),
     * get the caseloads data and populate the table
     */
    useEffect(() => {
        //Before anything, if the ids are empty, we go back so we don't crash the app
        if (state === undefined || state === null || state.ids === null || state.ids.length === 0) {
            history.goBack();
            return; //Make sure we return so the effect does not continue
        }

        setCaseloadIds(state.ids);

        if (state.ids.length > 0) {
            try {
                //We have to wrap the following async so we are able to use await
                const updateCaseloads = async () => {
                    const response = await getCaseloadsFromArray(state.ids);
                    const ids: number[] = [];

                    if (response.length > 0) {
                        const caseloadsArray: any[] = [];
                        response.map((data) => {
                            const caseload: Caseload | null = data.data.caseload;
                            if (caseload) caseloadsArray.push(caseload);
                            return true;
                        });
                        setCaseloads(caseloadsArray);

                        caseloadsArray.map((caseload: Caseload) => {
                            //document.getElementById(`caseload_${caseload.caseloadId}`)?.click();

                            //Get the discipline id
                            if (!ids.includes(caseload.disciplineId)) ids.push(caseload.disciplineId);

                            return true;
                        });
                        setDisciplineIds(ids);
                        setIsFetching(false);
                    }
                }

                //Calling the above async so we only set the caseloads after they are all returned
                setIsFetching(true);
                updateCaseloads();
            } catch (error: any) {
                console.log(error);
            }
        }

        setRemoveDate(removeDate ?? endOfMonth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.ids]);

    useEffect(() => {
        if (!removeOriginal) {
            setRemoveDate(null);
        } else {
            setRemoveDate(new Date())
        }
    }, [removeOriginal])

    /**
     * This effect updates the therapists every time the disciplineIds are chanegd
     */
    useEffect(() => {
        if (disciplineIds.length > 0) {
            const updateTherapists = async () => {
                const response = await getDisciplineGroupTherapistsByDisciplineIds(disciplineIds);
                const therapists = response.data;

                if (therapists.length > 0) {
                    let options: selectOption[] = [];
                    //set options           
                    therapists.map((t: any) => {
                        //make sure therapist aren't duplicated in the option list
                        if (!options.some(o => o.value === t.userId ?? -1))
                            options.push({ value: t.userId ?? -1, label: t.user?.lastName + ", " + t.user?.firstName });
                        return true;
                    });
                    options.sort((a, b) => a.label.localeCompare(b.label));
                    setTherapistsOptions(options.length > 0 ? options : fetchedTherapistsOptions);
                }

                setIsFetching(false);
            }

            //Calling the above async so we only set the caseloads after they are all returned
            setIsFetching(true);
            updateTherapists();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disciplineIds]);

    /**
     * Options lists (for our select elements)
     */
    /*const therapistsData = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => {
            let options: selectOption[] = therapistsOptions;
            const therapists: Therapist[] = u.data.therapists;

            //set options           
            (therapists.map((t: Therapist) => (
                options.push({ value: t.userId ?? -1, label: t.user?.lastName + ", " + t.user?.firstName })
            )));

            setFetchedTherapistsOptions(options);
        },
    });*/

    //Search button onClick handler
    const handleSave = async () => {
        let utc = null;
        if (removeDate) {
            var timezoneOffset = removeDate.getTimezoneOffset();
            utc = moment(removeDate).add(-timezoneOffset, 'm').toDate();
        }

        const request: CaseloadTransferRequest = {
            caseloadIds: selectedCaseloads,
            therapistId: therapist,
            startDate,
            removeDate: utc,
            removeOriginal,
            therapistName,
            disciplineName: disciplineName,
            disciplineId: disciplineId,
            districtName,
            schoolName,
            esy: null,
            placement: null,
            studentStatusId: null
        };
        try {
            const response = await transferCaseload(request);

            if (response.status === 200) {
                const allTransfers = response.data?.caseloadTransfers || [];

                const successfulTransfers = allTransfers.filter((t: any) => t.isSuccessful);
                const unsuccessfulTransfers = allTransfers.filter((t: any) => !t.isSuccessful);

                const successfulIds = successfulTransfers.map((t: any) => t.caseloadId);
                const unsuccessfulIds = unsuccessfulTransfers.map((t: any) => t.caseloadId);

                const successfulCount = successfulIds.length;
                const unsuccessfulCount = unsuccessfulIds.length;

                let message = '';

                if (successfulCount > 0) {
                    message += `Caseload IDs: ${successfulIds.join(', ')} Transfer Successful.\n`;
                }

                if (unsuccessfulCount > 0) {
                    const failureReason = unsuccessfulTransfers[0]?.message || "Transfer failed.";
                    message += `Caseload IDs: ${unsuccessfulIds.join(', ')} Failed. Reason: ${failureReason}`;
                }

                if (successfulCount > 0 && unsuccessfulCount === 0) {
                    // All successful – redirect
                    history.push('/caseloadTransfer', { message });
                    return;
                }

                // Set failed IDs for retry or display
                state.ids = unsuccessfulIds;
                state.message = message;
                setCaseloadIds(unsuccessfulIds);

            } else {
                state.message = 'Error transferring caseload.';
            }
        } catch (error: any) {
            state.message = `Error transferring caseload: ${error?.response?.data?.message || error.message || 'Unknown error.'}`;
        }
    };

    const transfer = useAsync({ deferFn: handleSave });

    const handleRowClick = (row: any) => {
        //Click the checkbox
        document.getElementById(`caseload_${row.values.caseloadId}`)?.click();

        //Rebuild selected caseloads
        const checkboxes = document.querySelectorAll('.datatable-checkbox');
        const sCaseloads: number[] = [];
        checkboxes.forEach((checkbox: any) => {
            if (checkbox.checked) sCaseloads.push(checkbox.value);
        });

        setSelectedCaseloads(sCaseloads);

        //Set whether or not we can transfer
        setCanTransfer(sCaseloads.length > 0);
    }

    /**
     * When the page size changes on the table and the select all button
     * is enabled, then check all the new checkboxes as well
     * @param pageSize number
     */
    const onChangePageSize = (pageSize: number) => {
        if (!isSelect) {
            document.querySelectorAll('.datatable-checkbox')
                .forEach((checkbox: any) => !checkbox.checked ? checkbox.click() : true);
        }
    }

    useEffect(() => {
        // Don't trigger fetch if therapist is still -1 (default)
        if (therapist === -1) return;

        const fetchDisciplines = async () => {
            try {
                setIsFetching(true);
                const response = await getTherapistByUserId(therapist);
                if (response.status === 200) {
                    let options: selectOption[] = [];
                    const disciplineGroupId = state.disciplineGroupId; // Assuming `state` is available and contains disciplineGroupId

                    // Map over therapists to populate discipline options
                    response.data.therapists!.forEach((g) => {
                        if (g.discipline!.disciplineGroupId === disciplineGroupId) {
                            const newOption = { value: g.disciplineId ?? -1, label: g.discipline?.name! };
                            if (!options.some(opt => opt.value === newOption.value)) {
                                options.push(newOption);
                            }
                        }
                    });

                    options.sort((a, b) => a.label.localeCompare(b.label));
                    setDisciplineOptions(options);
                }
            } catch (error) {
                console.error("Error fetching disciplines:", error);
            } finally {
                setIsFetching(false);
            }
        };

        fetchDisciplines();
    }, [therapist]); // Trigger effect when therapist is changed



    return (
        <Layout isLoading={
            transfer.isPending ||
            isFetching
        }>
            <Fragment>
                <Fragment>
                    {state?.message && <Notification message={state.message} isSuccess={false}></Notification>}

                    {/* Transfer Options (Select, checkbox, etc) */}
                    <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                        <div className='z-0 grid grid-cols-6 gap-6'>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Therapist {therapistsOptions.length}<span className="pl-1 text-red-500">*</span></label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={therapistsOptions}
                                    onChange={(e: any) => {
                                        const selectedTherapist = Number(e.value);
                                        if (selectedTherapist !== therapist) {  // Only set if it actually changed
                                            setTherapist(selectedTherapist);
                                        }
                                    }}
                                />
                            </div>



                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Disicpline<span className='pl-1 text-red-500'>*</span></label>
                                <SelectMulti
                                    className='block primary'
                                    isMulti={false}
                                    options={disciplineOptions}

                                    onChange={(e) => {
                                        setDisciplineName(e?.label!)
                                        if (disciplineId !== e?.value!) {
                                            setDisciplineId(e?.value!);
                                        }
                                    }

                                    }

                                />

                                {/* {disciplineOptions.sort((a, b) => {
                                        return a.label.localeCompare(b.label);
                                    }).map((item, idx) => <option key={`pageSize_${item.label}`}>{item.label}</option>)}
                                </select> */}
                            </div>



                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Start Date<span className='pl-1 text-red-500'>*</span></label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date: Date) => setStartDate(date!)}
                                    dateFormat="MM/dd/yyyy"
                                    required
                                />
                            </div>
                        </div>
                        <div className='z-0 grid grid-cols-6 gap-6 mt-6'>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <input
                                    className='mr-2'
                                    type='checkbox'
                                    checked={removeOriginal}
                                    onChange={(e: any) => setRemoveOriginal(e.target.checked)}
                                />
                                <label className='primary'>Remove Original Therapist From Caseload</label>
                            </div>
                            <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                                <label className='primary'>Remove Caseload As Of Date</label>
                                <DatePicker
                                    selected={removeDate}
                                    minDate={startDate}
                                    onChange={(date: Date) => setRemoveDate(date!)}
                                    dateFormat="MM/dd/yyyy"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Button row */}
                    <div className='flex px-4 py-3 text-right bg-gray-50 sm:px-6'>
                        <SubmitButton
                            text={isSelect ? 'Select All' : 'Unselect All'}
                            className='mr-5 bg-ptsblue-normal hover:bg-ptsblue-dark'
                            disabled={caseloads.length === 0}
                            onClick={() => {
                                const checkboxes = document.querySelectorAll('.datatable-checkbox');
                                //select or de-select based on the previous state
                                if (isSelect) {
                                    //select
                                    checkboxes.forEach((box) => {
                                        (box as HTMLInputElement).checked = true;
                                    });

                                    setSelectedCaseloads(caseloadIds);
                                    setIsSelect(false);
                                    setCanTransfer(true);
                                } else {
                                    //de-select
                                    checkboxes.forEach((box) => {
                                        (box as HTMLInputElement).checked = false;
                                    });

                                    setSelectedCaseloads([]);
                                    setIsSelect(true);
                                    setCanTransfer(false);
                                }
                            }}
                        />
                        <SubmitButton
                            text='Transfer'
                            className='mr-5 primary'
                            //disabled={therapist < 1 || !canTransfer}
                            disabled={therapist < 1 || !canTransfer || !disciplineId} // Disable if either field is not selected

                            onClick={() => { transfer.run() }}
                        />
                    </div>

                    {/* Caseloads display table */}
                    {caseloads.length > 0 && (
                        <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                            <DataTable
                                columns={columns}
                                data={caseloads}
                                showPagination={false}
                                getRowProps={(row: any) => ({
                                    onClick: () => { handleRowClick(row); }
                                })}
                                onChangePageSize={onChangePageSize}
                            />
                        </div>
                    )}

                    {/* No students found message */}
                    {caseloads.length === 0 && (
                        <div>
                            <label>No students found.</label>
                            <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                            </div>
                        </div>
                    )}
                </Fragment>
            </Fragment>
        </Layout>

    );
};

export default CaseloadsTransferStep2;